import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link } from "@tanstack/react-router";
import { Menu, X, ChevronDown } from "lucide-react";
import { Container } from "../layout/Container";
import { Button } from "@/components/ui/button";
import {
  NAV_PRIMARY,
  NAV_SECONDARY,
  isDropdown,
  type NavItem,
  type NavLink,
} from "../navLinks";
import { STRAPLINE } from "@/lib/brand";

function NavLinkItem({
  link,
  onClick,
}: {
  link: NavLink;
  onClick?: () => void;
}) {
  return (
    <Link
      to={link.to}
      className="text-sm font-medium text-white/80 transition hover:text-white"
      onClick={onClick}
    >
      {link.label}
    </Link>
  );
}

function DesktopDropdown({ item }: { item: NavItem }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!isDropdown(item)) {
    return <NavLinkItem link={item} />;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape" && open) {
      setOpen(false);
      buttonRef.current?.focus();
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onPointerEnter={(e) => {
        // Hover-open only for real mice: touch taps synthesize mouseenter
        // right before click, which would open-then-toggle-closed.
        if (e.pointerType === "mouse") setOpen(true);
      }}
      onPointerLeave={(e) => {
        if (e.pointerType === "mouse") setOpen(false);
      }}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-1 rounded text-sm font-medium text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-turquoise focus-visible:ring-offset-2 focus-visible:ring-offset-brand-blue-dark"
      >
        {item.label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`absolute left-0 top-full z-50 pt-2 transition-all ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div className="min-w-[200px] rounded-lg border border-white/10 bg-brand-blue-dark/95 p-2 shadow-xl backdrop-blur">
          {item.children.map((child) => (
            <Link
              key={child.to}
              to={child.to}
              onClick={() => setOpen(false)}
              className="block rounded-md px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavItem({
  item,
  onClose,
}: {
  item: NavItem;
  onClose: () => void;
}) {
  if (!isDropdown(item)) {
    return <NavLinkItem link={item} onClick={onClose} />;
  }

  return (
    <div className="space-y-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
        {item.label}
      </span>
      {item.children.map((child) => (
        <Link
          key={child.to}
          to={child.to}
          className="block pl-3 text-sm font-medium text-white/80 transition hover:text-white"
          onClick={onClose}
        >
          {child.label}
        </Link>
      ))}
    </div>
  );
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-white/10 bg-brand-blue-dark text-white shadow-lg">
      <Container className="flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/LarasoftLogoPortraitWhiteWithClearBackground.svg"
            alt="Larasoft"
            width={40}
            height={40}
            className="h-10 w-auto"
          />
          <span className="hidden lg:inline text-base font-bold text-white">
            {STRAPLINE}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex">
          {NAV_PRIMARY.map((item) => (
            <DesktopDropdown
              key={isDropdown(item) ? item.label : item.to}
              item={item}
            />
          ))}
          {NAV_SECONDARY.map((link) => (
            <NavLinkItem key={link.to} link={link} />
          ))}
          <Button
            asChild
            size="sm"
            className="rounded-full border border-white bg-transparent text-white hover:bg-white/10"
          >
            <Link to="/contact">Contact</Link>
          </Button>
          <Button
            asChild
            size="sm"
            className="rounded-full bg-brand-turquoise text-brand-blue-dark hover:bg-brand-turquoise/90"
          >
            <Link to="/portal/sign-in">Client portal</Link>
          </Button>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </Container>

      {/* Mobile nav */}
      {mobileMenuOpen && (
        <div className="border-t border-white/10 bg-brand-blue-dark md:hidden">
          <Container className="py-4">
            <nav className="flex flex-col gap-3">
              {NAV_PRIMARY.map((item) => (
                <MobileNavItem
                  key={isDropdown(item) ? item.label : item.to}
                  item={item}
                  onClose={() => setMobileMenuOpen(false)}
                />
              ))}
              {NAV_SECONDARY.map((link) => (
                <NavLinkItem
                  key={link.to}
                  link={link}
                  onClick={() => setMobileMenuOpen(false)}
                />
              ))}
              <Button
                asChild
                size="sm"
                className="w-full rounded-full border border-white bg-transparent text-white hover:bg-white/10"
              >
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  Contact
                </Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="w-full rounded-full bg-brand-turquoise text-brand-blue-dark hover:bg-brand-turquoise/90"
              >
                <Link
                  to="/portal/sign-in"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Client portal
                </Link>
              </Button>
            </nav>
          </Container>
        </div>
      )}
    </header>
  );
}
